<template>
  <div class="app-container">
    <Container
      :title="t('approvalForm.approvalFormList')"
      :hasLine="true"
    >
      <template #header>
        <SearchForm
          :formModel="searchFormModel"
          :formStruct="searchFormStruct"
          :hasCreate="false"
          :submitButton="t('common.search')"
          :resetButton="t('common.reset')"
          :label-width="labelWidth"
          @submit="submitSearch"
          @reset="resetForm"
        />
      </template>
      <template #main>
        <SimpleTable
          :listLoading="listLoading"
          :tableData="tableData"
          :columnStruct="columnStruct"
        >
          <template
            #default="slotProps"
          >
            <el-button
              @click="handleExamine(slotProps.row)"
              type="primary"
              size="mini"
            >
              {{ t('common.examine') }}
            </el-button>
          </template>
        </SimpleTable>
        <Pagination
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :page-size="limit"
          :current-page="page"
          :page-sizes="[10, 20, 50, 100, 200]"
          layout="total, sizes, prev, pager, next, jumper"
          style="text-align: right;"
        />
      </template>
    </Container>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, toRefs, onMounted, computed, onActivated } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatDate } from '@/utils'
import { createModel } from '@/utils/component'
import { checkPermission } from '@/utils/permission'
import Container from '@/components/form/container/Container.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import { ApprovalFormModel, ApprovalFormPageParamModel } from '@/model/process/approvalModel'
import { useRouter } from 'vue-router'
import { getApprovalFormPageList, getApprovalFormPageListByUser } from '@/apis/process/approval'
import dayjs from 'dayjs'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'
import resize from '@/layout/resize'
import Pagination from '@/components/pagination/Pagination.vue'
import { DeviceType } from '@/store/modules/app/state'

export default defineComponent({
  name: 'ApprovalFormPageList',
  components: {
    Pagination,
    SearchForm,
    SimpleTable,
    Container
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const { device } = resize()
    const labelWidth = computed(() => {
      if (device.value === DeviceType.Mobile) {
        return '100px'
      } else {
        return '150px'
      }
    })
    const searchStruct = [
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      {
        prop: 'status',
        label: t('table.status'),
        component: 'el-select',
        options: [
          { label: t('approvalForm.toBeSubmitted'), value: 0 },
          { label: t('approvalForm.pendingLevel1Approval'), value: 1 },
          { label: t('approvalForm.pendingLevel2Approval'), value: 2 },
          { label: t('approvalForm.pendingLevel3Approval'), value: 3 }
        ]
      },
      { prop: 'deadline', label: t('approvalForm.deadline'), component: 'el-date-picker', type: 'date', model: '' },
      { prop: 'approver', label: t('approvalForm.currentApprover'), component: 'el-input', type: 'text', model: '' }
    ]
    const formatStatus = (status: number | null) => {
      if (status === 0) {
        return t('approvalForm.toBeSubmitted')
      } else if (status === 1) {
        return t('approvalForm.pendingLevel1Approval')
      } else if (status === 2) {
        return t('approvalForm.pendingLevel2Approval')
      } else if (status === 3) {
        return t('approvalForm.pendingLevel3Approval')
      }
      return null
    }
    const formatApprover = (approvers: string | null) => {
      if (approvers != null && approvers.length > 0) {
        return approvers.replaceAll(',', ' / ')
      }
      return null
    }
    const approvalformTableStruct = [
      { prop: 'id', label: t('approvalForm.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'userName', label: t('approvalForm.submitter') },
      {
        prop: 'submitDate',
        label: t('approvalForm.submitDate'),
        formatter: (row: any) => formatDate(row.submitDate, 'yyyy-MM-dd')
      },
      {
        prop: 'approver',
        label: t('approvalForm.currentApprover'),
        formatter: (row: any) => formatApprover(row.approver)
      },
      {
        prop: 'deadline',
        label: t('approvalForm.deadline'),
        formatter: (row: any) => formatDate(row.deadline, 'yyyy-MM-dd')
      },
      { prop: 'status', label: t('table.status'), formatter: (row: any) => formatStatus(row.status) }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as ApprovalFormPageParamModel,
      tableData: Array<ApprovalFormModel>(),
      columnStruct: approvalformTableStruct,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true
        let deadline = null
        if (dataMap.searchFormModel.deadline) {
          deadline = dayjs(dataMap.searchFormModel.deadline).endOf('day').format('YYYY-MM-DD HH:mm:ss SSS')
        }
        const params: ApprovalFormPageParamModel = {
          companyCode: dataMap.searchFormModel.companyCode,
          status: dataMap.searchFormModel.status,
          deadline: deadline,
          approver: dataMap.searchFormModel.approver,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        // const data = await getApprovalFormPageList(params)
        let data = null
        if (checkPermission(['APPROVAL_PLAN_R'])) {
          data = await getApprovalFormPageList(params)
        } else if (checkPermission(['APPROVAL_PLAN_SR'])) {
          data = await getApprovalFormPageListByUser(params)
        } else {
          data = await getApprovalFormPageList(params)
        }
        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list
        } else {
          dataMap.tableData = []
        }
        dataMap.total = data?.data.totalCount ?? 0

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      }
    })
    const handleExamine = (row: ApprovalFormModel) => {
      router.push({ path: `approvalFormDetail/${row.id}` })
    }
    const getCompanyList = async() => {
      dataMap.searchFormStruct[0].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({
          label: iterator.companyCode + ', ' + iterator.name,
          value: iterator.companyCode
        })
      }
      dataMap.searchFormStruct[0].options = optionList
      dataMap.searchFormStruct[0].loading = false
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
      getCompanyList()
    })
    onActivated(async() => {
      if (history.state?.refresh === true) {
        await dataMap.getPageList()
        // 用完删除
        const state = { ...history.state }
        delete state.refresh
        history.replaceState(state, '')
      }
    })
    return {
      t,
      labelWidth,
      handleExamine,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style
  scoped
  lang="scss"
>

</style>
